import { useEffect, useRef } from "react";

type UseRefreshOnReturnOptions = {
  enabled?: boolean;
  minAwayMs?: number;
};

/**
 * Calls `onReturn` when the user comes back to this tab after being away.
 */

export const useRefreshOnReturn = (
  onReturn: () => void,
  { enabled = true, minAwayMs = 3000 }: UseRefreshOnReturnOptions = {}
) => {
  // Hold the callback in a ref so a re-render (new closure every time) does not tear the
  // listeners down and back up, which would drop an in-progress "away" timestamp.
  const onReturnRef = useRef(onReturn);
  onReturnRef.current = onReturn;

  useEffect(() => {
    if (!enabled) return;

    // Non-null while the user is away. Seeded for the case where the hook mounts on an
    // already-hidden tab (a background bfcache restore, for instance).
    let awaySince: number | null =
      document.visibilityState === "hidden" ? Date.now() : null;

    const markAway = () => {
      // Keep the earliest timestamp: leaving for Word fires blur *and* (sometimes)
      // visibilitychange, and the second one must not reset the clock.
      if (awaySince === null) awaySince = Date.now();
    };

    const markReturned = () => {
      if (awaySince === null) return;
      const awayFor = Date.now() - awaySince;
      // Cleared before the callback so a focus + visibilitychange pair on the way back
      // refreshes once, not twice.
      awaySince = null;
      if (awayFor >= minAwayMs) onReturnRef.current();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") markAway();
      else markReturned();
    };

    const handlePageShow = (event: PageTransitionEvent) => {
      // Only a bfcache restore needs this — a normal load already fetches on mount.
      if (!event.persisted) return;
      awaySince = null;
      onReturnRef.current();
    };

    window.addEventListener("blur", markAway);
    window.addEventListener("focus", markReturned);
    window.addEventListener("pageshow", handlePageShow);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("blur", markAway);
      window.removeEventListener("focus", markReturned);
      window.removeEventListener("pageshow", handlePageShow);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [enabled, minAwayMs]);
};

export default useRefreshOnReturn;
